/**
 * PaymentMethodAcquirerSwitchDialog — 策略组「切换收单方」批量动作（ADM-PAY-S008，A8 §7.8.2「切换收单方」）
 *
 * 两步：① 选策略组 / 源通道 / 目标通道 → 「重读并预览」先重读该组（§7.8.1，绕过缓存）；
 * ② 按重读结果算替换计划（`~/data/adapters/payment-method-acquirer-switch`），展示替换项（原 → 新）、保留项（附原因）、
 * 最终成员集合与重读时刻，运营确认后由面板以最终集合调 §7.8.2 整组替换。
 *
 * 提交是**无条件整组保存**：重读之后他人对该组的修改会被覆盖、不自动合并 —— 契约要求确认页标明重读时刻，
 * 故②页把时刻与这条后果写在最前。卡品牌用固定图标、切换不要求图标；替换出的非卡品牌目标行仍受 §7.8.2 图标前置
 * 校验，这里与编辑弹窗同口径前移：最终集合里有已知缺图标、同品牌冲突、已退役的成员时禁用确认并写明原因。
 *
 * 目标通道是框架通道（如 PayerMax）时，②页另读该通道的配置列表（A8 §10.1，viewer+）判本 App 的**收单前置**：
 * 配置缺失、未启用、未连接或原生 App 缺回跳地址 → 警示「C 端不会下发该通道的支付方式」；切换本身不阻断（契约
 * 写侧不做 serving 校验）。读取失败显示「无法判定」，不当成已满足（`AcquiringPrerequisiteNote`）。
 *
 * 视觉以设计稿 design/Novel-admin/src/pages/app-payment-acquirer-switch.jsx 为准（评审页 PaymentMethodAcquirerSwitch.html）。
 */

import type { PaymentMethodOption, PaymentMethodPolicyResponse } from '@novel-admin/shared';
import { Fragment, type ReactNode, createElement, useState } from 'react';
import {
  type AcquirerSwitchKeptReason,
  acquirerSwitchSourceChannels,
  acquirerSwitchTargetChannels,
  planAcquirerSwitch,
} from '~/data/adapters/payment-method-acquirer-switch';
import {
  type PanelBrandPlatform,
  panelBrandConflictsIn,
} from '~/data/adapters/payment-method-panel-brand';
import { fmtDateTimeSec } from '~/lib/format';
import { Icons } from '~/lib/icons';
import { Dialog, DialogBody } from '~/ui/Dialog';
import { FormRow } from '~/ui/FormRow';
import { Select } from '~/ui/Select';
import { Table } from '~/ui/Table';
import { AcquiringPrerequisiteNote } from './AcquiringPrerequisiteNote';
import { type PaymentMethodOrderItem, PaymentMethodOrderStrip } from './PaymentMethodOrderPreview';
import { NOT_SELECTABLE_BADGE, channelDisplayLabel } from './payment-method-labels';

/** 保留原因 → 运营可读说明 */
const KEPT_REASON_TEXT: Record<AcquirerSwitchKeptReason, string> = {
  OTHER_CHANNEL: '非源通道成员，原样保留',
  NO_BRAND: '无面板品牌（如整个供应商收银台），原样保留',
  NO_TARGET: '目标通道没有同品牌的可选支付方式，保留',
  AMBIGUOUS_TARGET: '目标通道有多个同品牌支付方式，无法唯一替换，保留',
  TARGET_ALREADY_PRESENT: '同品牌的目标支付方式已在本组，保留',
  NOT_SELECTABLE: `${NOT_SELECTABLE_BADGE}，查不到通道与品牌，保留`,
};

/** 重读快照：重读到的组 + 重读时刻 + 当时选定的通道（冻结，列表随重读刷新也不漂移） */
interface RereadSnapshot {
  policy: PaymentMethodPolicyResponse;
  readAt: string;
  sourceChannel: string;
  targetChannel: string;
}

/** 提示块：info = 流程说明，warn = 覆盖后果 / 阻断原因，neg = 重读失败（设计稿 app-payment-acquirer-switch.jsx `Note`） */
function SwitchNote({
  tone,
  icon,
  children,
}: {
  tone: 'info' | 'warn' | 'neg';
  icon: keyof typeof Icons;
  children: ReactNode;
}) {
  return (
    <div
      className="hstack"
      style={{
        gap: 8,
        alignItems: 'flex-start',
        padding: '10px 12px',
        borderRadius: 7,
        background: `var(--${tone}-soft)`,
        color: tone === 'info' ? 'var(--fg)' : `var(--${tone})`,
        fontSize: 11.5,
        lineHeight: 1.6,
      }}
    >
      <span style={{ flexShrink: 0, marginTop: 2 }}>
        {createElement(Icons[icon], { size: 12 })}
      </span>
      <div>{children}</div>
    </div>
  );
}

/** 小节标题：名称 + 计数 chip + 可选说明 */
function SwitchSectionHead({
  title,
  count,
  hint,
}: {
  title: string;
  count?: number;
  hint?: string;
}) {
  return (
    <div className="hstack" style={{ gap: 6, marginBottom: 6 }}>
      <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--fg-strong)' }}>{title}</span>
      {count !== undefined && (
        <span className="chip" style={{ fontSize: 10 }}>
          {count}
        </span>
      )}
      {hint && <span style={{ fontSize: 11, color: 'var(--fg-faint)' }}>{hint}</span>}
    </div>
  );
}

/** 步骤条：放在 Dialog `header` 槽，两步固定 */
function SwitchSteps({ step }: { step: 1 | 2 }) {
  return (
    <div
      className="hstack"
      style={{
        gap: 8,
        padding: '8px 18px',
        borderBottom: '1px solid var(--border)',
        background: 'var(--bg-subtle)',
      }}
    >
      {(['选择通道', '核对并确认'] as const).map((name, index) => {
        const n = index + 1;
        const state = n < step ? 'done' : n === step ? 'active' : 'todo';
        return (
          <Fragment key={name}>
            {index > 0 && (
              <span style={{ color: 'var(--fg-faint)' }}>
                <Icons.IconChevRight size={11} />
              </span>
            )}
            <span
              className={state === 'active' ? 'chip chip-accent' : 'chip'}
              style={{ fontSize: 10.5, opacity: state === 'todo' ? 0.6 : 1 }}
              aria-current={state === 'active' ? 'step' : undefined}
            >
              {state === 'done' ? <Icons.IconCheck size={9} /> : <span className="mono">{n}</span>}
              {name}
            </span>
          </Fragment>
        );
      })}
    </div>
  );
}

/** 通道键：替换前后对照用的等宽小字 */
function SwitchChannelTag({ channel }: { channel: string | undefined }) {
  return channel === undefined ? null : (
    <span className="mono" style={{ fontSize: 10.5, color: 'var(--fg-faint)', marginLeft: 6 }}>
      {channel}
    </span>
  );
}

export function PaymentMethodAcquirerSwitchDialog({
  appId,
  policies,
  options,
  platform,
  scopeLabelOf,
  labelOf,
  methodsWithIcon,
  toOrderItems,
  saving,
  onReread,
  onSubmit,
  onClose,
}: {
  /** 所属 App：收单前置按它取目标通道配置行 */
  appId: string;
  /** 页面已加载的策略组（只用于选组；计划一律按重读结果算） */
  policies: readonly PaymentMethodPolicyResponse[];
  /** §7.8.8 可选支付方式 */
  options: readonly PaymentMethodOption[];
  platform: PanelBrandPlatform | null;
  scopeLabelOf: (regionCode: string | null) => string;
  labelOf: (method: string) => string;
  /** 已满足图标要求的支付方式（含卡品牌固定图标）；`null` = 设置未就绪，不据此拦 */
  methodsWithIcon: Set<string> | null;
  /** 最终成员 → 预览项（图标 / 底色 / 未启用标示与组行预览同源） */
  toOrderItems: (methods: readonly string[]) => PaymentMethodOrderItem[];
  saving: boolean;
  /** 重读该组（§7.8.1，绕过缓存）；组已不存在返 null，读失败抛错 */
  onReread: (regionCode: string | null) => Promise<PaymentMethodPolicyResponse | null>;
  onSubmit: (regionCode: string | null, finalMembers: string[]) => void;
  onClose: () => void;
}) {
  const memberOf = (policy: PaymentMethodPolicyResponse) =>
    policy.payment_methods.map((item) => item.payment_method);
  // 组候选：至少有一个可切换（有品牌）成员的组
  const groupChoices = policies.filter(
    (policy) => acquirerSwitchSourceChannels(memberOf(policy), options).length > 0,
  );
  const [pickedPolicyId, setPickedPolicyId] = useState('');
  const [pickedSource, setPickedSource] = useState('');
  const [pickedTarget, setPickedTarget] = useState('');
  // 选中项失效（组被删 / 通道不再出现）时退回第一个候选，不让运营面对空选择
  const activePolicy =
    groupChoices.find((policy) => policy.policy_id === pickedPolicyId) ?? groupChoices[0] ?? null;
  const sourceChoices = activePolicy
    ? acquirerSwitchSourceChannels(memberOf(activePolicy), options)
    : [];
  const activeSource = sourceChoices.includes(pickedSource)
    ? pickedSource
    : (sourceChoices[0] ?? null);
  const targetChoices =
    activeSource === null ? [] : acquirerSwitchTargetChannels(options, activeSource);
  const activeTarget = targetChoices.includes(pickedTarget)
    ? pickedTarget
    : (targetChoices[0] ?? null);

  const [snapshot, setSnapshot] = useState<RereadSnapshot | null>(null);
  const [rereading, setRereading] = useState(false);
  const [rereadError, setRereadError] = useState<string | null>(null);
  const busy = saving || rereading;

  const reread = async () => {
    if (!activePolicy || activeSource === null || activeTarget === null) return;
    const sourceChannel = activeSource;
    const targetChannel = activeTarget;
    setRereading(true);
    setRereadError(null);
    try {
      const latest = await onReread(activePolicy.region_code);
      if (latest === null) {
        setRereadError('该策略组已被删除，请关闭弹窗后刷新列表');
        return;
      }
      setSnapshot({
        policy: latest,
        readAt: new Date().toISOString(),
        sourceChannel,
        targetChannel,
      });
    } catch (err) {
      setRereadError(`重读策略组失败 · ${err instanceof Error ? err.message : '请重试'}`);
    } finally {
      setRereading(false);
    }
  };

  const plan = snapshot
    ? planAcquirerSwitch({
        members: memberOf(snapshot.policy),
        options,
        sourceChannel: snapshot.sourceChannel,
        targetChannel: snapshot.targetChannel,
        platform,
      })
    : null;
  const optionByMethod = new Map(options.map((option) => [option.payment_method, option]));
  const finalMembers = plan?.finalMembers ?? [];
  const brandConflicts = panelBrandConflictsIn(
    finalMembers,
    (method) => optionByMethod.get(method),
    platform,
  );
  const missingIcon =
    methodsWithIcon === null ? [] : finalMembers.filter((m) => !methodsWithIcon.has(m));
  const notSelectable = finalMembers.filter((m) => !optionByMethod.has(m));
  const blockReason =
    plan === null
      ? null
      : plan.replaced.length === 0
        ? '没有可替换的成员：本次切换不改变该组'
        : brandConflicts.length > 0
          ? '最终成员里仍有同一面板品牌的两家供应商，保存会被拒绝'
          : notSelectable.length > 0
            ? `最终成员含${NOT_SELECTABLE_BADGE}的支付方式，请先在「编辑」里移除`
            : missingIcon.length > 0
              ? '最终成员里有支付方式尚未配置图标，保存会被拒绝'
              : null;

  const scopeText = snapshot ? scopeLabelOf(snapshot.policy.region_code) : '';
  const channelOf = (method: string) => optionByMethod.get(method)?.channel;
  // 页脚左侧状态说明：①「不会直接保存」；② 阻断原因优先，否则给替换 / 保留计数
  const footerNote =
    snapshot === null
      ? '下一步先重读该组，不会直接保存'
      : (blockReason ??
        (plan ? `将替换 ${plan.replaced.length} 项，保留 ${plan.kept.length} 项` : ''));

  return (
    <Dialog
      closeOnEsc={!busy}
      title={snapshot ? `确认切换收单方 · ${scopeText}` : '切换收单方'}
      subtitle="把策略组内源通道的各品牌成员整组替换为目标通道的同品牌支付方式，次序不变。"
      icon="IconRefresh"
      width={760}
      header={<SwitchSteps step={snapshot === null ? 1 : 2} />}
      onClose={() => {
        if (!busy) onClose();
      }}
      footer={
        <>
          <div
            style={{
              flex: 1,
              fontSize: 11,
              color: blockReason !== null ? 'var(--warn)' : 'var(--fg-muted)',
            }}
          >
            {footerNote}
          </div>
          <button type="button" className="btn" disabled={busy} onClick={onClose}>
            取消
          </button>
          {snapshot === null ? (
            <button
              type="button"
              className="btn btn-accent"
              disabled={busy || activePolicy === null || activeTarget === null}
              onClick={() => void reread()}
            >
              <Icons.IconRefresh size={11} />
              {rereading ? '重读中…' : '重读并预览'}
            </button>
          ) : (
            <>
              <button
                type="button"
                className="btn"
                disabled={busy}
                onClick={() => setSnapshot(null)}
              >
                返回修改
              </button>
              <button
                type="button"
                className="btn btn-accent"
                disabled={busy || blockReason !== null}
                onClick={() => onSubmit(snapshot.policy.region_code, finalMembers)}
              >
                <Icons.IconCheck size={11} />
                {saving ? '保存中…' : '确认切换并保存'}
              </button>
            </>
          )}
        </>
      }
    >
      {snapshot === null ? (
        <DialogBody gap={12}>
          {groupChoices.length === 0 ? (
            <div style={{ fontSize: 11.5, color: 'var(--fg-muted)' }}>
              没有可切换的策略组：各组都没有带面板品牌的成员。
            </div>
          ) : (
            <>
              <FormRow label="策略组" labelWidth={120} hint="只列出含带品牌成员的组">
                <Select
                  value={activePolicy?.policy_id ?? ''}
                  onChange={(e) => setPickedPolicyId(e.target.value)}
                  name="switch_policy"
                  disabled={busy}
                  style={{ maxWidth: 320 }}
                >
                  {groupChoices.map((policy) => (
                    <option key={policy.policy_id} value={policy.policy_id}>
                      {scopeLabelOf(policy.region_code)}
                    </option>
                  ))}
                </Select>
              </FormRow>
              <FormRow
                label="源通道"
                labelWidth={120}
                hint="组内当前成员所属、且有带品牌成员的通道"
              >
                <Select
                  value={activeSource ?? ''}
                  onChange={(e) => setPickedSource(e.target.value)}
                  name="switch_source_channel"
                  disabled={busy}
                  style={{ maxWidth: 320 }}
                >
                  {sourceChoices.map((channel) => (
                    <option key={channel} value={channel}>
                      {channelDisplayLabel(channel)}
                    </option>
                  ))}
                </Select>
              </FormRow>
              <FormRow
                label="目标通道"
                labelWidth={120}
                hint="可选支付方式里有带品牌目录行的其他通道"
              >
                {targetChoices.length === 0 ? (
                  <span style={{ fontSize: 11.5, color: 'var(--fg-muted)' }}>
                    没有可切换到的其他通道。
                  </span>
                ) : (
                  <Select
                    value={activeTarget ?? ''}
                    onChange={(e) => setPickedTarget(e.target.value)}
                    name="switch_target_channel"
                    disabled={busy}
                    style={{ maxWidth: 320 }}
                  >
                    {targetChoices.map((channel) => (
                      <option key={channel} value={channel}>
                        {channelDisplayLabel(channel)}
                      </option>
                    ))}
                  </Select>
                )}
              </FormRow>
              <SwitchNote tone="info" icon="IconInfo">
                下一步会先重读该组的最新成员，再按重读结果列出替换项、保留项与最终次序，确认后才保存。
              </SwitchNote>
              {rereadError !== null && (
                <SwitchNote tone="neg" icon="IconAlert">
                  {rereadError}
                </SwitchNote>
              )}
            </>
          )}
        </DialogBody>
      ) : (
        plan !== null && (
          <DialogBody gap={16}>
            <SwitchNote tone="warn" icon="IconClock">
              重读时刻 <strong className="mono">{fmtDateTimeSec(snapshot.readAt)}</strong>
              。确认后以下方最终成员<strong>无条件整组覆盖保存</strong>
              ：重读之后他人对该组的修改会被覆盖，不会自动合并。
            </SwitchNote>
            <div className="hstack" style={{ gap: 8, fontSize: 12 }}>
              <span className="chip">{channelDisplayLabel(snapshot.sourceChannel)}</span>
              <Icons.IconArrowRight size={12} />
              <span className="chip chip-accent">
                {channelDisplayLabel(snapshot.targetChannel)}
              </span>
            </div>

            <div>
              <SwitchSectionHead title="替换项" count={plan.replaced.length} />
              {plan.replaced.length === 0 ? (
                <div style={{ fontSize: 11.5, color: 'var(--fg-faint)' }}>没有可替换的成员。</div>
              ) : (
                <Table className="tbl" resizable={false}>
                  <thead>
                    <tr>
                      <th style={{ width: '44%' }}>原成员</th>
                      <th style={{ width: 28 }} aria-label="替换为" />
                      <th>替换为</th>
                    </tr>
                  </thead>
                  <tbody>
                    {plan.replaced.map((item) => (
                      <tr key={item.from}>
                        <td>
                          {labelOf(item.from)}
                          <SwitchChannelTag channel={channelOf(item.from)} />
                        </td>
                        <td style={{ color: 'var(--fg-faint)' }}>
                          <Icons.IconArrowRight size={11} />
                        </td>
                        <td>
                          <div className="hstack" style={{ gap: 6 }}>
                            <span>
                              {labelOf(item.to)}
                              <SwitchChannelTag channel={channelOf(item.to)} />
                            </span>
                            {missingIcon.includes(item.to) && (
                              <span className="chip chip-warn" style={{ fontSize: 10 }}>
                                缺图标
                              </span>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </div>

            <div>
              <SwitchSectionHead title="保留项" count={plan.kept.length} hint="不参与本次切换" />
              {plan.kept.length === 0 ? (
                <div style={{ fontSize: 11.5, color: 'var(--fg-faint)' }}>无。</div>
              ) : (
                <Table className="tbl" resizable={false}>
                  <thead>
                    <tr>
                      <th style={{ width: '44%' }}>成员</th>
                      <th>原因</th>
                    </tr>
                  </thead>
                  <tbody>
                    {plan.kept.map((item) => (
                      <tr key={item.method}>
                        <td>
                          {labelOf(item.method)}
                          <SwitchChannelTag channel={channelOf(item.method)} />
                        </td>
                        <td style={{ color: 'var(--fg-muted)' }}>
                          {KEPT_REASON_TEXT[item.reason]}
                          <span
                            className="mono"
                            style={{ fontSize: 10, color: 'var(--fg-faint)', marginLeft: 6 }}
                          >
                            {item.reason}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </div>

            <div>
              <SwitchSectionHead
                title="最终次序"
                hint="自左向右即 C 端展示次序；高亮为本次替换进来的成员"
              />
              {finalMembers.length === 0 ? (
                <div style={{ fontSize: 11.5, color: 'var(--fg-faint)' }}>空。</div>
              ) : (
                <PaymentMethodOrderStrip
                  items={toOrderItems(finalMembers)}
                  highlighted={new Set(plan.replaced.map((item) => item.to))}
                />
              )}
            </div>

            <AcquiringPrerequisiteNote
              appId={appId}
              channel={snapshot.targetChannel}
              isNativeApp={platform === null ? null : platform !== 'WEB'}
              actionLabel="切换"
              // 保存在途不离开（与关闭按钮同一保护）；其余情况只是丢掉本次重读预览，直接跳转
              onConfigLinkClick={(_to, event) => {
                if (busy) event.preventDefault();
              }}
            />
            {brandConflicts.length > 0 && (
              <SwitchNote tone="warn" icon="IconAlert">
                {brandConflicts
                  .map(({ earlier, member }) => `${labelOf(earlier)} 与 ${labelOf(member)}`)
                  .join('；')}{' '}
                是同一面板品牌，同一策略组内一个品牌只能有一家供应商（A8
                §7.8.2）。请先在「编辑」里移除其一。
              </SwitchNote>
            )}
            {missingIcon.length > 0 && (
              <SwitchNote tone="warn" icon="IconAlert">
                <strong>{missingIcon.map((m) => labelOf(m)).join('、')}</strong>{' '}
                尚未配置图标，本次保存会被服务端拒绝（A8 §7.8.2「策略成员 ⇒
                有图标」）。请先在上方「支付方式图标」上传。
              </SwitchNote>
            )}
          </DialogBody>
        )
      )}
    </Dialog>
  );
}
